import { clx } from "@hm/fundamental"
import { useCallback, useEffect, useRef } from "react"

import type { GridCell } from "../types"

interface DaysGridProps {
	weekdays: string[]
	daysGrid: GridCell[]
	isDaySelected: (day: number) => boolean
	isDayInRange: (day: number) => boolean
	onDayClick: (day: number) => void
	onDayHover: (day: number) => void
	onMouseLeave: () => void
	"data-cy": string
}

export const DaysGrid: React.FC<DaysGridProps> = ({
	weekdays,
	daysGrid,
	isDaySelected,
	isDayInRange,
	onDayClick,
	onDayHover,
	onMouseLeave,
	"data-cy": dataCy,
}) => {
	const gridRef = useRef<HTMLTableElement>(null)

	const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
		const allButtons = Array.from(
			gridRef.current?.querySelectorAll<HTMLButtonElement>("button") ??
				[],
		)
		const currentIndex = allButtons.indexOf(e.target as HTMLButtonElement)
		if (currentIndex === -1) return

		let nextIndex: number | null = null

		switch (e.key) {
			case "ArrowRight":
				nextIndex = currentIndex + 1
				break
			case "ArrowLeft":
				nextIndex = currentIndex - 1
				break
			case "ArrowDown":
				nextIndex = currentIndex + 7
				break
			case "ArrowUp":
				nextIndex = currentIndex - 7
				break
			default:
				return
		}

		if (
			nextIndex !== null &&
			nextIndex >= 0 &&
			nextIndex < allButtons.length &&
			!allButtons[nextIndex].disabled
		) {
			e.preventDefault()
			allButtons[nextIndex].focus()
		}
	}, [])

	useEffect(() => {
		const el = gridRef.current
		if (!el) return
		const handler = () => onMouseLeave()
		el.addEventListener("mouseleave", handler)
		return () => el.removeEventListener("mouseleave", handler)
	}, [onMouseLeave])

	const tabbableDay =
		daysGrid.find(c => c.isCurrentMonth && isDaySelected(c.day))?.day ??
		daysGrid.find(c => c.isCurrentMonth)?.day ??
		null

	const rows: GridCell[][] = []
	for (let i = 0; i < daysGrid.length; i += 7) {
		rows.push(daysGrid.slice(i, i + 7))
	}

	return (
		<table
			ref={gridRef}
			aria-label="Calendar"
			className="w-full border-collapse"
			data-cy={`${dataCy}-days-grid`}
		>
			<thead>
				<tr>
					{weekdays.map(wd => (
						<th
							key={wd}
							scope="col"
							className="text-center text-xs font-medium py-1 text-foreground opacity-60"
						>
							{wd}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{rows.map(row => (
					<tr key={`${row[0].isCurrentMonth}-${row[0].day}`}>
						{row.map((cell, colIdx) => {
							const isWeekend = colIdx >= 5
							const selected =
								cell.isCurrentMonth && isDaySelected(cell.day)
							const inRange =
								cell.isCurrentMonth && isDayInRange(cell.day)

							return (
								<td
									key={`${cell.isCurrentMonth}-${cell.day}`}
									aria-selected={selected}
									className="p-0"
								>
									<button
										type="button"
										disabled={!cell.isCurrentMonth}
										onKeyDown={handleKeyDown}
										tabIndex={
											cell.isCurrentMonth &&
											cell.day === tabbableDay
												? 0
												: -1
										}
										onClick={() =>
											cell.isCurrentMonth &&
											onDayClick(cell.day)
										}
										onMouseEnter={() =>
											cell.isCurrentMonth &&
											onDayHover(cell.day)
										}
										className={clx(
											"h-9 w-full text-center text-sm rounded-sm transition-colors",
											!cell.isCurrentMonth &&
												"text-gray-300 cursor-default",
											cell.isCurrentMonth &&
												!selected &&
												"cursor-pointer hover:bg-primary_faded_20",
											cell.isCurrentMonth &&
												isWeekend &&
												!selected &&
												"text-gray-400",
											inRange &&
												!selected &&
												"bg-primary_faded_20",
											selected &&
												"!bg-primary !text-white",
										)}
										data-cy={
											cell.isCurrentMonth
												? `${dataCy}-day-${cell.day}`
												: undefined
										}
									>
										{cell.day}
									</button>
								</td>
							)
						})}
					</tr>
				))}
			</tbody>
		</table>
	)
}
